// Backup Tab Component for AdminPage (Import/Export)
import React, { useState } from 'react';
import { Download, Upload, RefreshCw, AlertCircle, Check, FileSpreadsheet, Table } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import * as api from '../../services/api';

const BackupTab = ({ loadData }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [csvImportType, setCsvImportType] = useState('empresas');
  const [csvData, setCsvData] = useState([]);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');

  const handleExportAll = async () => {
    try {
      setIsExporting(true);
      const response = await api.exportAllData();
      const data = response.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `infinito_crm_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('Backup descargado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Error al exportar datos');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const text = await file.text();
      const data = JSON.parse(text);
      
      const merge = window.confirm(
        '¿Desea combinar los datos con los existentes?\n\n' +
        'SI: Los datos se combinarán (registros duplicados se actualizarán)\n' +
        'NO: Los datos existentes serán reemplazados'
      );

      const response = await api.importAllData(data, merge);
      setImportStats(response.data);
      loadData();
      alert('Datos importados correctamente');
    } catch (error) {
      console.error('Error importing:', error);
      alert('Error al importar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  const parseCSVExcel = async (file) => {
    const fileName = file.name.toLowerCase();
    
    if (fileName.endsWith('.csv')) {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) return { columns: [], data: [] };
      
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((h, i) => row[h] = values[i] || '');
        return row;
      });
      return { columns: headers, data };
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            if (!window.XLSX) {
              const script = document.createElement('script');
              script.src = 'https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js';
              document.head.appendChild(script);
              await new Promise(res => script.onload = res);
            }
            const data = new Uint8Array(e.target.result);
            const workbook = window.XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = window.XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const columns = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
            resolve({ columns, data: jsonData });
          } catch (err) {
            console.error('Excel parse error:', err);
            resolve({ columns: [], data: [] });
          }
        };
        reader.readAsArrayBuffer(file);
      });
    }
    return { columns: [], data: [] };
  };

  const handleCSVFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCsvFileName(file.name);
    const { columns, data } = await parseCSVExcel(file);
    setCsvColumns(columns);
    setCsvData(data);
    setShowCSVImportModal(true);
    e.target.value = '';
  };

  const handleCSVImport = async () => {
    if (csvData.length === 0) {
      alert('No hay datos para importar');
      return;
    }
    
    try {
      setIsImporting(true);
      const isMultiType = csvColumns.includes('type');
      
      let response;
      if (isMultiType) {
        response = await api.importFromCSVMultitype(csvData);
      } else {
        response = await api.importFromCSV({
          import_type: csvImportType,
          records: csvData,
          merge: true
        });
      }
      
      setImportStats(response.data);
      setShowCSVImportModal(false);
      setCsvData([]);
      setCsvColumns([]);
      loadData();
      
      if (isMultiType) {
        const imp = response.data.imported || {};
        alert(`Importación completada:\n- Usuarios: ${imp.users || 0}\n- Empresas: ${imp.empresas || 0}\n- Contactos: ${imp.contacts || 0}\n- Notas: ${imp.notes || 0}\n- Tareas: ${imp.tasks || 0}\n- Embudos: ${imp.funnels || 0}\n- Deals: ${imp.funnel_companies || 0}`);
      } else {
        alert(`Importación completada: ${response.data.imported} registros importados`);
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Error al importar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Export Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Download size={20} className="text-[#D4A843]" />
              Exportar Datos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Descarga una copia de seguridad completa en formato JSON.
            </p>
            <div className="p-3 bg-gray-50 rounded-lg mb-4 text-sm">
              <p className="text-gray-600">Incluye: Usuarios, Empresas, Contactos, Tareas, Notas, Deals, Evaluaciones, Plantillas, Roles</p>
            </div>
            <Button 
              onClick={handleExportAll} 
              disabled={isExporting}
              className="w-full bg-[#D4A843] hover:bg-[#c49a3a]"
              data-testid="export-all-btn"
            >
              {isExporting ? (
                <><RefreshCw size={16} className="mr-2 animate-spin" /> Exportando...</>
              ) : (
                <><Download size={16} className="mr-2" /> Descargar Backup</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import JSON Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Upload size={20} className="text-blue-600" />
              Importar JSON
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Restaura datos desde un backup JSON exportado.
            </p>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
              <p className="text-xs text-yellow-700">
                <AlertCircle size={12} className="inline mr-1" />
                Puedes combinar o reemplazar datos existentes
              </p>
            </div>
            <input type="file" accept=".json" onChange={handleImportFile} className="hidden" id="import-file-input" />
            <label htmlFor="import-file-input">
              <Button asChild disabled={isImporting} className="w-full bg-blue-600 hover:bg-blue-700 cursor-pointer">
                <span data-testid="import-btn">
                  {isImporting ? (
                    <><RefreshCw size={16} className="mr-2 animate-spin" /> Importando...</>
                  ) : (
                    <><Upload size={16} className="mr-2" /> Importar JSON</>
                  )}
                </span>
              </Button>
            </label>
          </CardContent>
        </Card>

        {/* Import CSV/Excel Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-green-600" />
              Importar CSV/Excel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Importa datos desde archivos CSV o Excel (.xlsx).
            </p>
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <p className="text-xs text-green-700">
                <Table size={12} className="inline mr-1" />
                Soporta: Empresas, Contactos, Tareas
              </p>
            </div>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleCSVFileSelect} className="hidden" id="import-csv-input" />
            <label htmlFor="import-csv-input">
              <Button asChild className="w-full bg-green-600 hover:bg-green-700 cursor-pointer">
                <span>
                  <FileSpreadsheet size={16} className="mr-2" /> Importar CSV/Excel
                </span>
              </Button>
            </label>
          </CardContent>
        </Card>
      </div>

      {/* Import Stats */}
      {importStats && (
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Check size={18} className="text-green-500" />
              <h4 className="font-medium text-green-800">Importación completada</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {importStats.imported !== undefined && typeof importStats.imported === 'number' ? (
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Registros importados</p>
                  <p className="font-semibold text-lg">{importStats.imported}</p>
                </div>
              ) : (
                Object.entries(importStats.imported || {}).map(([key, value]) => (
                  <div key={key} className="p-2 bg-gray-50 rounded">
                    <p className="text-gray-500 capitalize">{key}</p>
                    <p className="font-semibold text-lg">{value}</p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CSV Import Modal */}
      <Dialog open={showCSVImportModal} onOpenChange={setShowCSVImportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar {csvFileName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {csvColumns.includes('type') ? (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <AlertCircle size={14} className="inline mr-1" />
                  Archivo multi-tipo detectado. Se importarán todos los registros automáticamente.
                </p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-gray-700">Tipo de datos</label>
                <Select value={csvImportType} onValueChange={setCsvImportType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="empresas">Empresas</SelectItem>
                    <SelectItem value="contacts">Contactos</SelectItem>
                    <SelectItem value="tasks">Tareas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Vista previa ({csvData.length} registros)</p>
              <div className="max-h-64 overflow-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      {csvColumns.slice(0, 6).map((col) => (
                        <th key={col} className="p-2 text-left font-medium">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t">
                        {csvColumns.slice(0, 6).map((col) => (
                          <td key={col} className="p-2 truncate max-w-32">{row[col]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {csvData.length > 10 && (
                <p className="text-xs text-gray-500 mt-1">...y {csvData.length - 10} registros más</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCSVImportModal(false)}>Cancelar</Button>
            <Button onClick={handleCSVImport} disabled={isImporting} className="bg-green-600 hover:bg-green-700">
              {isImporting ? 'Importando...' : `Importar ${csvData.length} registros`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BackupTab;
